 
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FileInfo,
  FileInfoResponse,
  PayloadOriginFile,
  StreamFile,
  UpdateModelIdPayload,
} from "@/common/define";
import { SystemConstants } from "@/common/system-constants";
import { Observable, of } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { ajax, AjaxResponse } from "rxjs/ajax";
import { APIHosts, ApiReduxHelpers } from "./reduxHelpers";
import { StatusTask } from "@/features/upload/stores/uploadFiles/uploadFileSlice";
import HttpClient from "./HttpClient";

export interface ConvertOptions {
  convert3DModel: 0 | 1;
  convert2DSheet: 0 | 1;
  extractProperties: 0 | 1;
  childModels: 0;
}

export interface ConvertFilePayload {
  filename: string;
  originalFilePath?: string;
  convertOptions: ConvertOptions;
  downloadUrl?: string;
  baseFileId: string;
  baseMajorRev: number;
  baseMinorRev: number;
}

export interface TaskLog {
  id: string;
  taskId: string;
  log: string;
  createTime: string;
  action: string;
  type: string;
  message: string;
}

/** Mirrors conversion-api's OverallJobStatus enum (Conversion/Models/PipelineJob.cs). */
export enum PipelineOverallStatus {
  Queued = 0,
  Preparing = 1,
  Running = 2,
  Completed = 3,
  PartialSuccess = 4,
  Failed = 5,
}

/** Subset of conversion-api's PipelineJob response actually needed for a status badge. */
export interface PipelineJobSummary {
  jobId: string;
  overallStatus: PipelineOverallStatus;
  convertType?: string;
  completedAt?: string | null;
}

export interface RetryByFileRevisionPayload {
  baseFileId: string;
  baseMajorRev: number;
  baseMinorRev: number;
  subPipelineName?: string;
  allowCompleted?: boolean;
  allowStuckActive?: boolean;
}

/** Thrown when conversion-api answers 409 with retryable:true (FullReconvertDeferredException) --
 * a previous dispatch for the same job is still being published; safe to retry after the given
 * delay, not a real failure. */
export class ReconvertDeferredError extends Error {
  constructor(public retryAfterSeconds: number) {
    super(`Reconvert temporarily deferred, retry after ${retryAfterSeconds}s`);
  }
}

class ConversionApiController extends ApiReduxHelpers {
  constructor() {
    super();
  }
  ApiHost = APIHosts.Conversion;

  getCacheByListFile(
    data: PayloadOriginFile[]
  ): Observable<FileInfoResponse[]> {
    const url = `${this.getHost(this.ApiHost)}/${
      SystemConstants.SERVER_CONVERSION.API.CACHE.GET_LIST_FILE
    }`;

    return HttpClient.post(url, data).pipe(
      map((res) => res as FileInfoResponse[])
    );
  }

  updateModelId(data: UpdateModelIdPayload): Observable<number> {
    const url = `${this.getHost(this.ApiHost)}/${
      SystemConstants.SERVER_CONVERSION.API.CACHE.UPDATE_MODEL_FILE_ID
    }`;

    return HttpClient.put(url, data).pipe(map((res) => res as number));
  }

  getAllCache(): Observable<any[]> {
    const url = `${this.getHost(this.ApiHost)}/${
      SystemConstants.SERVER_CONVERSION.API.CACHE.GET_ALL
    }`;

    return HttpClient.get(url).pipe(map((res) => res as any[]));
  }

  cacheFile(data: StreamFile) {
    const path = "?overwrite=true&ignore_line_weight=1";
    const url = `${this.getHost(this.ApiHost)}/${
      SystemConstants.SERVER_CONVERSION.API.CACHE.GET
    }${path}`;

    return HttpClient.post(url, data).pipe(
      map((res) => res as FileInfoResponse)
    );
  }

convertFileByProject(
  projectName: string,
  data: ConvertFilePayload,
  overwrite = true
): Observable<FileInfoResponse> {
      const path = `?overwrite=${overwrite}&project=${encodeURIComponent(
      projectName
    )}`;
      const url = `${this.getHost(this.ApiHost)}/${
      SystemConstants.SERVER_CONVERSION.API.CACHE.CONVERT
    }${path}`;
  return ajax.post(url, data).pipe(
    map((res) => res.response as FileInfoResponse)
  );
}

  getTaskByBaseFileId(baseFileId: string): Observable<StatusTask> {
    const url = `${this.getHost(this.ApiHost)}/${SystemConstants.SERVER_CONVERSION.API.CACHE.GET_TASK}?basefileid=${baseFileId}`;
    return HttpClient.get(url).pipe(
      map((res) => res as StatusTask)
    );
  }

  /**
   * Real, currently-working pipeline status (conversion-control's PipelineJob) -- distinct from
   * getTaskByBaseFileId above (the older ConversionTask model, which the real Rvt2Scz pipeline
   * never populates). Returns null when no job exists yet for this file (404, e.g. a non-RVT
   * file, or an RVT that hasn't been through the convert trigger yet) instead of throwing, since
   * that's an expected, common case here -- not an error.
   *
   * The endpoint returns an ARRAY of every job for this baseFileId, sorted newest first (a file
   * can be reconverted, producing multiple jobs over time) -- this returns just the newest one.
   */
  getPipelineByBaseFileId(
    baseFileId: string
  ): Observable<PipelineJobSummary | null> {
    const url = `${this.getHost(this.ApiHost)}/${
      SystemConstants.SERVER_CONVERSION.API.PIPELINE.GET_BY_BASE_FILE_ID
    }/${encodeURIComponent(baseFileId)}`;
    console.debug(
      "[conversion.api] getPipelineByBaseFileId: fetching",
      baseFileId
    );
    return HttpClient.get(url).pipe(
      map((res) => {
        console.debug(
          "[conversion.api] getPipelineByBaseFileId: ok",
          baseFileId,
          res
        );
        const jobs = res as PipelineJobSummary[] | null;
        return Array.isArray(jobs) && jobs.length > 0 ? jobs[0] : null;
      }),
      catchError((err) => {
        if (err?.status === 404) {
          console.debug(
            "[conversion.api] getPipelineByBaseFileId: no job found",
            baseFileId
          );
          return of(null);
        }
        console.error(
          "[conversion.api] getPipelineByBaseFileId: failed",
          baseFileId,
          err
        );
        throw err;
      })
    );
  }

  getTaskLogByTaskId(taskId: string): Observable<TaskLog[]> {
  const url = `${this.getHost(this.ApiHost)}/${SystemConstants.SERVER_CONVERSION.API.CACHE.GET_LOG}?taskId=${taskId}`;
  return HttpClient.get(url).pipe(
    map((res) => res as TaskLog[])
  );
}

  /**
   * Reconvert an existing file (no fresh upload). Looks up the newest/active PipelineJob for
   * this baseFileId+revision server-side and re-runs it -- AllowCompleted/AllowStuckActive
   * default true server-side, so this also reopens an already-Completed job.
   */
  retryByFileRevision(
    payload: RetryByFileRevisionPayload
  ): Observable<PipelineJobSummary> {
    const url = `${this.getHost(this.ApiHost)}/${
      SystemConstants.SERVER_CONVERSION.API.PIPELINE.RETRY_BY_FILE_REVISION
    }`;
    console.debug("[conversion.api] retryByFileRevision: requesting", payload);
    return HttpClient.post(url, payload).pipe(
      map((res) => {
        console.debug("[conversion.api] retryByFileRevision: ok", res);
        return res as PipelineJobSummary;
      }),
      catchError((err) => {
        // FullReconvertDeferredException surfaces as 409 { retryable: true, retryAfterSeconds }
        // -- a previous dispatch for this job is still publishing, not a real failure.
        if (err?.status === 409 && err?.response?.retryable) {
          console.debug(
            "[conversion.api] retryByFileRevision: deferred",
            err.response
          );
          throw new ReconvertDeferredError(
            err.response.retryAfterSeconds ?? 30
          );
        }
        console.error("[conversion.api] retryByFileRevision: failed", payload, err);
        throw err;
      })
    );
  }

  getFileInfoByBaseFiles(
    baseFiles: { baseFileId: string }[]
  ): Observable<FileInfoResponse[]> {
    const url = `${this.getHost(this.ApiHost)}/${SystemConstants.SERVER_CONVERSION.API.CACHE.POST}/info`;

    return HttpClient.post(url, baseFiles).pipe(
      map((res) => res as FileInfoResponse[])
    );
  }


  uploadFile(data: any, path: string): Observable<string> {
    const url = `${this.getHost(this.ApiHost)}/${
      SystemConstants.SERVER_CONVERSION.API.FILE.UPLOAD
    }?path=${path}`;

    return HttpClient.upload(url, data).pipe(map((res) => res as string));
  }

  clearFileCached(id: string): Observable<number> {
    const newHeaders = { Accept: "text/plain" };
    const api = SystemConstants.SERVER_CONVERSION.API.CACHE.DELETE(id);
    const url = `${this.getHost(this.ApiHost)}/${api}`;

    return HttpClient.delete(url, { headers: newHeaders }).pipe(
      map((res) => res as number)
    );
  }

  addFolder(path: string): Observable<boolean> {
    const data = ``;
    const api = `${SystemConstants.SERVER_CONVERSION.API.FILE.ADD}?path=${path}`;
    const url = `${this.getHost(this.ApiHost)}/${api}`;

    return HttpClient.post(url, data).pipe(map((res) => res as boolean));
  }
}

export const ConversionApi = new ConversionApiController();
